import { ChangeDetectionStrategy, Component, computed, inject, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormBuilder, FormControl, ReactiveFormsModule } from '@angular/forms';

import { merge, take } from 'rxjs';

import { InfoboxComponent } from '@core/components-infobox-legacy';
import { injectTranslate, TranslatePipe } from '@core/i18n-legacy';
import { HiringResourcesDataService } from '@core/tellent-assign-members-to-jobs-logic';
import {
  AssignMembersToJobsDialogData,
  AssignMembersToJobsDialogResult,
  AssignMembersToJobsExtraPayload,
  HiringResource,
  HiringResourceKind,
} from '@core/tellent-assign-members-to-jobs-types';
import {
  HiringResourceSelectComponent,
  MemberAvatarComponent,
} from '@core/tellent-assign-members-to-jobs-ui';
import { TellentAccessService, TellentMembersDataFacade } from '@core/tellent-logic';
import { InviteMemberPayload, TellentMember } from '@core/tellent-types';
import { resolveMemberFullName, TellentComponentsNotificationService } from '@core/tellent-util';
import {
  BadgeComponent,
  ButtonComponent,
  ButtonLoadingDirective,
  DialogBodyComponent,
  DialogComponent,
  DialogFooterComponent,
  DialogHeaderComponent,
  Select2Item,
} from '@core/user-interface-legacy';
import { injectUntilDestroyed } from '@core/utils-legacy';

@Component({
  selector: 'tlc-assign-members-to-jobs-dialog',
  templateUrl: './assign-members-to-jobs-dialog.component.html',
  styleUrls: ['./assign-members-to-jobs-dialog.component.less'],
  imports: [
    DialogHeaderComponent,
    TranslatePipe,
    DialogBodyComponent,
    DialogFooterComponent,
    ButtonLoadingDirective,
    ButtonComponent,
    BadgeComponent,
    ReactiveFormsModule,
    HiringResourceSelectComponent,
    MemberAvatarComponent,
    InfoboxComponent,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [HiringResourcesDataService],
})
export class AssignMembersToJobsDialogComponent extends DialogComponent<
  AssignMembersToJobsDialogData,
  AssignMembersToJobsDialogResult
> {
  private readonly fb = inject(FormBuilder);
  private readonly dataService = inject(HiringResourcesDataService);
  private readonly tellentAccessService = inject(TellentAccessService);
  private readonly membersDataFacade = inject(TellentMembersDataFacade);
  private readonly notificationService = inject(TellentComponentsNotificationService);
  private readonly untilDestroyed = injectUntilDestroyed();
  private readonly t = injectTranslate();

  public readonly jobs = toSignal(this.dataService.jobs$, { initialValue: [] });
  public readonly talentPools = toSignal(this.dataService.talentPools$, {
    initialValue: [],
  });
  public readonly jobsLoaded = toSignal(this.dataService.jobsLoaded$, { initialValue: false });
  public readonly talentPoolsLoaded = toSignal(this.dataService.talentPoolsLoaded$, {
    initialValue: false,
  });
  public readonly HiringResourceKind = HiringResourceKind;

  public form = this.fb.group({
    jobsSelect: new FormControl<HiringResource[]>([]),
    talentPoolsSelect: new FormControl<HiringResource[]>([]),
  });

  public addActionPayload: InviteMemberPayload;
  public members: Array<Pick<TellentMember, 'email' | 'firstName' | 'lastName'>> = [];
  public recruiteeRoleId: string;
  public kiwiRoleId: string;

  public loading = signal<boolean>(false);

  protected jobsItems = computed(() => this.getItems(this.jobs()));
  protected talentPoolsItems = computed(() => this.getItems(this.talentPools()));

  public setDialogData(data: AssignMembersToJobsDialogData) {
    this.addActionPayload = data.payload;
    this.members = [...data.payload.members];
    this.recruiteeRoleId = data.recruiteeRoleId;
    this.kiwiRoleId = data.kiwiRoleId;
  }

  public confirm(): void {
    this.submitMembers(this.prepareExtendedPayload());
  }

  public getItems(options: HiringResource[] | null): Select2Item[] {
    if (!options) return [];

    return options.map(option => ({
      label:
        option.title ??
        this.t('tlc.assign_members_to_jobs.dialog.untitled_resource', { id: option.guid }),
      value: option,
    }));
  }

  private submitMembers(actionPayload: InviteMemberPayload): void {
    this.loading.set(true);

    if (this.tellentAccessService.canMangeTellentUsers()) {
      this.membersDataFacade.inviteTellentMember(actionPayload);
    } else {
      this.membersDataFacade.inviteAppMember(actionPayload);
    }

    this.handleSubmitSuccess();
    this.handleSubmitFailure();
  }

  private prepareExtendedPayload(): InviteMemberPayload {
    const jobs = this.getResourcesIds(this.form.value.jobsSelect!);
    const talentPools = this.getResourcesIds(this.form.value.talentPoolsSelect!);
    const actionPayload = this.addActionPayload;

    if (jobs.length === 0 && talentPools.length === 0) return actionPayload!;

    const extraPayload: AssignMembersToJobsExtraPayload = {
      hiringResources: {
        jobs: [...jobs],
        talentPools: [...talentPools],
      },
    };

    actionPayload?.members?.forEach(member => (member.extraPayload = extraPayload));

    return { ...actionPayload! };
  }

  private getResourcesIds(hiringResources: HiringResource[]): string[] {
    return hiringResources.map(resource => resource.recruitmentId);
  }

  private handleSubmitSuccess(): void {
    merge(
      this.membersDataFacade.inviteTellentMemberSuccess$,
      this.membersDataFacade.inviteAppMemberSuccess$,
    )
      .pipe(take(1), this.untilDestroyed)
      .subscribe(({ payload }) => {
        this.form.markAsPristine();

        this.loading.set(false);
        const { firstName, lastName, email } = this.addActionPayload?.members[0]!;
        this.notificationService.success(
          this.t('tlc.members.add_member.success_toast', {
            count: this.addActionPayload?.members.length,
            fullName: resolveMemberFullName({
              firstName: firstName || '',
              lastName: lastName || '',
              email: email || '',
            }),
          }),
        );

        this.dialogRef.close({ success: true, payload });
      });
  }

  private handleSubmitFailure(): void {
    merge(
      this.membersDataFacade.inviteTellentMemberFail$,
      this.membersDataFacade.inviteAppMemberFail$,
    )
      .pipe(take(1), this.untilDestroyed)
      .subscribe(() => this.loading.set(false));
  }
}
